import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import api from '../lib/api';
import { toast } from 'sonner';
import { History, Search, User, Package, FileText, Truck, Settings } from 'lucide-react';
import { useCompany } from '../contexts/CompanyContext';

const AuditLogs = () => {
  const { activeCompany, companies } = useCompany();
  const companyMap = React.useMemo(() => {
    const m = {};
    (companies || []).forEach(c => { m[c.company_id] = c; });
    return m;
  }, [companies]);
  const renderCompanyBadge = (cid) => {
    const c = cid ? companyMap[cid] : null;
    const code = c?.code || '—';
    return (
      <Badge variant="outline" className="text-xs font-semibold" title={c?.name || 'Legacy / untagged'}>
        {code}
      </Badge>
    );
  };
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    entity_type: 'all',
    action: 'all'
  });

  useEffect(() => {
    fetchLogs();
  }, [filters, activeCompany?.company_id]);

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.entity_type !== 'all') params.append('entity_type', filters.entity_type);
      if (filters.action !== 'all') params.append('action', filters.action);
      params.append('limit', '200');
      
      const response = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(response.data);
      setLoading(false);
    } catch (error) {
      toast.error('Failed to load audit logs');
      setLoading(false);
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'grey_order': return <Package className="h-4 w-4 text-blue-600" />;
      case 'grey_bill': return <FileText className="h-4 w-4 text-green-600" />;
      case 'lr_report': return <Truck className="h-4 w-4 text-amber-600" />;
      case 'grey_receipt': return <Package className="h-4 w-4 text-teal-600" />;
      case 'supplier':
      case 'quality':
      case 'subquality':
      case 'transport':
        return <Settings className="h-4 w-4 text-slate-600" />;
      default: return <History className="h-4 w-4 text-slate-600" />;
    }
  };

  const getActionBadge = (action) => {
    switch (action) {
      case 'CREATE':
        return <Badge className="bg-green-100 text-green-700 border-green-200">Created</Badge>;
      case 'UPDATE':
        return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Updated</Badge>;
      case 'DELETE':
        return <Badge className="bg-red-100 text-red-700 border-red-200">Deleted</Badge>;
      case 'LR_EDIT_WITH_RECEIPTS':
        return <Badge className="bg-amber-100 text-amber-700 border-amber-200">LR Edited (Receipts Linked)</Badge>;
      case 'RECEIPT_MATERIAL_QTY_BACKFILL':
        return <Badge className="bg-teal-100 text-teal-700 border-teal-200">Material Qty Backfill</Badge>;
      default:
        return <Badge variant="outline">{action}</Badge>;
    }
  };

  const FIELD_LABELS = {
    order_id: 'Order', bill_id: 'Bill', bales_in_lr: 'Bales',
    material_quantity: 'Material Quantity', material_unit: 'Material Unit',
    lr_number_transport: 'Bilty Number', transport_id: 'Transport', lr_date: 'LR Date'
  };

  const renderDetails = (log) => {
    const nv = log.new_value || {};
    if (Array.isArray(nv.field_changes) && nv.field_changes.length > 0) {
      return (
        <div className="space-y-1 text-xs">
          {nv.field_changes.map((c, i) => (
            <div key={i} className="text-slate-600">
              <span className="font-medium text-slate-800">{FIELD_LABELS[c.field] || c.field}:</span>{' '}
              <span className="line-through text-red-500">{String(c.old ?? '—')}</span>{' → '}
              <span className="text-green-600 font-medium">{String(c.new ?? '—')}</span>
            </div>
          ))}
        </div>
      );
    }
    if (log.action === 'RECEIPT_MATERIAL_QTY_BACKFILL') {
      return (
        <span className="text-xs text-slate-600">
          Filled {nv.filled_value} {nv.material_unit} into {nv.receipt_ids?.length || 0} blank receipt(s) from {nv.lr_number}
        </span>
      );
    }
    return <span className="text-slate-300">—</span>;
  };

  const formatEntityType = (type) => {
    return type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || type;
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div data-testid="audit-logs-page" className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Audit Logs
        </h1>
        <p className="text-slate-600 mt-1">Track all changes made in the system</p>
      </div>

      {/* Filters */}
      <Card className="mb-6 border border-slate-200">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Entity Type</Label>
              <Select value={filters.entity_type} onValueChange={(v) => setFilters({...filters, entity_type: v})}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="grey_order">Grey Orders</SelectItem>
                  <SelectItem value="grey_bill">Grey Bills</SelectItem>
                  <SelectItem value="lr_report">LR Reports</SelectItem>
                  <SelectItem value="grey_receipt">Grey Receipts</SelectItem>
                  <SelectItem value="supplier">Suppliers</SelectItem>
                  <SelectItem value="quality">Qualities</SelectItem>
                  <SelectItem value="subquality">Finish Qualities</SelectItem>
                  <SelectItem value="transport">Transports</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Action</Label>
              <Select value={filters.action} onValueChange={(v) => setFilters({...filters, action: v})}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="CREATE">Created</SelectItem>
                  <SelectItem value="UPDATE">Updated</SelectItem>
                  <SelectItem value="DELETE">Deleted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <p className="text-sm text-slate-500">{logs.length} records found</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="border border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Entity</TableHead>
                  <TableHead>ID</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Date & Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length > 0 ? (
                  logs.map((log) => (
                    <TableRow key={log.log_id || `${log.entity_type}-${log.entity_id}-${log.timestamp}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getEntityIcon(log.entity_type)}
                          <span className="font-medium">{formatEntityType(log.entity_type)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-sm">{log.entity_id}</TableCell>
                      <TableCell data-testid="audit-company-badge">{renderCompanyBadge(log.company_id)}</TableCell>
                      <TableCell>{getActionBadge(log.action)}</TableCell>
                      <TableCell className="max-w-xs">{renderDetails(log)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-400" />
                          <span>{log.user_name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{formatDate(log.created_at)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                      <History className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                      <p>No audit logs found</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogs;
